import React, { useRef, useEffect } from 'react';
import { Box, Popover, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { theme, ThemedTypography, ThemedButton } from '../../themes/technicalAlerts';

// Base popover wrapper with consistent styling
const BasePopover = ({
  open,
  anchorEl,
  onClose,
  children,
  minWidth = 160,
  placement = { vertical: 'bottom', horizontal: 'center' }
}) => {
  // Ref for popover scroll container
  const popoverRef = useRef(null);

  // Popover is in a portal (outside main scroll container).
  // MUI components capture wheel events for scroll-to-change behavior.
  // Solution: Intercept in capture phase, stop propagation, manually scroll page.
  useEffect(() => {
    const popoverElement = popoverRef.current;
    if (!popoverElement || !open) return;

    const handleWheel = (e) => {
      if (!popoverElement.contains(e.target)) return;

      // CRITICAL: Stop event from reaching MUI components that would block scroll
      e.stopPropagation();

      // Manually scroll the main page content
      const pageScrollContainer = document.querySelector('.css-4z6nar') || document.documentElement;
      pageScrollContainer.scrollTop += e.deltaY;
    };

    // Use capture phase to intercept before MUI's handlers
    popoverElement.addEventListener('wheel', handleWheel, { capture: true, passive: false });

    return () => {
      popoverElement.removeEventListener('wheel', handleWheel, { capture: true });
    };
  }, [open]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={placement}
      transformOrigin={{
        vertical: 'top',
        horizontal: placement.horizontal,
      }}
      PaperProps={{
        ref: popoverRef
      }}
      sx={{
        zIndex: 1500, // Higher than theme.zIndex.popover to ensure it's above everything
        '& .MuiPaper-root': {
          overflow: 'visible',
          zIndex: 1500
        },
        '& .MuiPopover-paper': {
          maxWidth: 'calc(100vw - 16px)'
        }
      }}
    >
      <Box sx={{ p: 2, minWidth }}>
        {children}
      </Box>
    </Popover>
  );
};

// Confirmation popover for Yes/No questions
export const ConfirmationPopover = ({
  open,
  anchorEl,
  onClose,
  title,
  onConfirm,
  onCancel,
  confirmText = 'yes',
  cancelText = 'no',
  confirmColor = 'error'
}) => {
  const { t } = useTranslation();
  
  return (
    <BasePopover open={open} anchorEl={anchorEl} onClose={onClose}>
      <Box sx={{
        display: 'flex',
        gap: '8px',
        justifyContent: 'center',
        alignItems: 'center',
        py: '4px'
      }}>
        {/* Question text inline with buttons - matching card layout */}
        <ThemedTypography.Caption sx={{
          fontSize: '10px',
          color: theme.colors.text.primary,
          fontWeight: 500,
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          mr: '4px'
        }}>
          {t(title)}
        </ThemedTypography.Caption>
        
        {/* Buttons styled to match card IconButtons */}
        <Button 
          size="small" 
          variant="outlined"
          onClick={onCancel}
          sx={{
            minWidth: '24px',
            width: '24px',
            height: '24px',
            backgroundColor: 'transparent',
            border: '1px solid rgba(107, 114, 128, 0.2)', // Subtle gray border like card
            borderRadius: '4px', // Smaller radius like card
            color: '#8E8E93', // Apple gray like card
            fontSize: '9px',
            fontWeight: 500,
            padding: 0,
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: 'rgba(107, 114, 128, 0.05)',
              borderColor: 'rgba(34, 197, 94, 0.3)',
            },
            '&:active': {
              transform: 'scale(0.98)'
            }
          }}
        >
          {t(cancelText)}
        </Button>
        <Button 
          size="small" 
          variant="outlined"
          onClick={onConfirm}
          sx={{
            minWidth: '24px',
            width: '24px',
            height: '24px',
            backgroundColor: 'transparent',
            border: '1px solid rgba(107, 114, 128, 0.2)', // Subtle gray border like card
            borderRadius: '4px', // Smaller radius like card
            color: '#8E8E93', // Apple gray like card
            fontSize: '9px',
            fontWeight: 500,
            padding: 0,
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: 'rgba(107, 114, 128, 0.05)',
              borderColor: 'rgba(249, 115, 22, 0.3)',
            },
            '&:active': {
              transform: 'scale(0.98)'
            }
          }}
        >
          {t(confirmText)}
        </Button>
      </Box>
    </BasePopover>
  );
};

// Generic action popover with custom content
export const ActionPopover = ({
  open,
  anchorEl,
  onClose,
  title,
  children,
  actions = [],
  minWidth = 160
}) => {
  const { t } = useTranslation();
  
  return (
    <BasePopover open={open} anchorEl={anchorEl} onClose={onClose} minWidth={minWidth}>
      {title && (
        <ThemedTypography.Caption sx={{ mb: 1.5, fontWeight: 600 }}>
          {t(title)}
        </ThemedTypography.Caption>
      )}
    
    {children}
    
    {actions.length > 0 && (
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        {actions.map((action, index) => (
          <Button
            key={index}
            size="small"
            variant={action.variant || 'outlined'}
            color={action.color || 'primary'}
            onClick={action.onClick}
            disabled={action.disabled}
            sx={{ 
              ...theme.typography.micro,
              ...(action.sx || {})
            }}
          >
            {action.label}
          </Button>
        ))}
      </Box>
    )}
    </BasePopover>
  );
};

// Hook for managing popover state
export const usePopoverState = () => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [popoverType, setPopoverType] = React.useState(null);
  const [popoverData, setPopoverData] = React.useState({});

  const openPopover = React.useCallback((event, type, data = {}) => {
    setAnchorEl(event.currentTarget);
    setPopoverType(type);
    setPopoverData(data);
  }, []);

  const closePopover = React.useCallback(() => {
    setAnchorEl(null);
    setPopoverType(null);
    setPopoverData({});
  }, []);

  const isOpen = (type = null) => {
    if (type) return Boolean(anchorEl) && popoverType === type;
    return Boolean(anchorEl);
  };

  return {
    anchorEl,
    popoverType,
    popoverData,
    openPopover,
    closePopover,
    isOpen
  };
};

// Pre-configured popovers for common Technical Alerts use cases
export const TechnicalAlertsPopovers = {
  // Review popover for broken slot confirmation
  Review: ({ open, anchorEl, onClose, onConfirmBroken, onConfirmNotBroken }) => (
    <ConfirmationPopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      title="isThisBroken"
      onConfirm={() => onConfirmBroken?.()}
      onCancel={() => onConfirmNotBroken?.()}
      confirmText="yes"
      cancelText="no"
      confirmColor="error"
    />
  )
};

export default BasePopover;
